import React, { useState } from 'react';
import { fmt, fmtCount } from '../../utils/formatters';
import {
  CONDITION_ORDER, CONDITION_SHORT, CallOutcome, OUTCOME_WORDS, RecordCall, byDate, callOutcome, dayLabel,
  moneyRangeWords, outcomeCounts,
} from '../../utils/weather';
import { FOCUS, MICRO, NOTE } from './weatherUi';

// The tally: one small square per call, oldest first, read like a win-loss
// record. Forest filled for right, dusty rose for missed, hollow for "the
// weather never came", faint for not scored yet. The legend counts each in
// words, and a tapped square says its day in a line under the squares.

export const OUTCOME_SQUARE: Record<CallOutcome, string> = {
  right: 'bg-[#2E6B4F]',
  missed: 'bg-[#B54A4A]',
  never: 'border border-[#6B6670] bg-white',
  open: 'bg-[#6B6670]/30',
};
const ORDER: CallOutcome[] = ['right', 'missed', 'never', 'open'];
const EMPTY_SLOTS = 12;

const sentenceCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A square's words: "Tue, Sep 22: missed". */
export const squareTitle = (c: RecordCall): string => `${dayLabel(c.for_date)}: ${OUTCOME_WORDS[callOutcome(c)]}`;

/** The line under the squares for a tapped call. */
function detail(c: RecordCall): string {
  const expected = `expected ${moneyRangeWords(c.usual + c.dollars_low, c.usual + c.dollars_high)}`;
  const sold = c.actual != null ? `, sold ${fmt(c.actual)}` : '';
  return `${sentenceCase(squareTitle(c))}. ${sentenceCase(c.call)}, ${expected}${sold}.`;
}

function Square({ c, active, onPick }: { c: RecordCall; active: boolean; onPick: () => void }) {
  const outcome = callOutcome(c);
  return (
    <li>
      {/* A 24px target around a 12px square; neighbours' targets overlap by 6px so the squares sit 18px apart. */}
      <button
        type="button" data-square={c.for_date} data-outcome={outcome} title={squareTitle(c)} aria-label={squareTitle(c)}
        aria-pressed={active} onClick={onPick} onMouseEnter={onPick}
        className={`-mx-[3px] flex h-6 w-6 items-center justify-center rounded-[6px] ${FOCUS}`}
      >
        <span data-mark className={`block h-3 w-3 rounded-[3px] ${OUTCOME_SQUARE[outcome]} ${active ? 'ring-2 ring-[#4A2D5E] ring-offset-1' : ''}`} />
      </button>
    </li>
  );
}

function EmptyFrame() {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
      <ul aria-hidden="true" className="flex shrink-0 flex-wrap gap-[5px]">
        {Array.from({ length: EMPTY_SLOTS }, (_, i) => (
          <li key={i} data-empty-slot className="h-3 w-3 rounded-[3px] border border-dashed border-[#6B6670]/40" />
        ))}
      </ul>
      <p className="max-w-[46ch] font-sans text-sm leading-[1.6] text-[#2D2A2E]">
        No weather calls scored yet.{' '}
        <span className="text-[#6B6670]">The first one is graded the morning after the next day the weather moves your sales.</span>
      </p>
    </div>
  );
}

export default function RecordTally({ calls }: { calls: RecordCall[] }) {
  const [picked, setPicked] = useState<string | null>(null);
  if (!calls.length) return <EmptyFrame />;
  const conditions: string[] = CONDITION_ORDER.filter((k) => calls.some((c) => c.condition === k));
  const extra = Array.from(new Set(calls.map((c) => c.condition))).filter((k) => !conditions.includes(k));
  const groups = conditions.concat(extra).map((condition) => ({ condition, calls: byDate(calls.filter((c) => c.condition === condition)) }));
  const counts = outcomeCounts(calls);
  const keyOf = (c: RecordCall) => `${c.for_date}|${c.condition}`;
  const current = calls.find((c) => keyOf(c) === picked);
  return (
    <div data-tally>
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.condition} data-tally-group={g.condition}>
            {groups.length > 1 && <p className={`${MICRO} mb-1.5`}>{CONDITION_SHORT[g.condition] ?? g.condition}</p>}
            <ul aria-label={`${CONDITION_SHORT[g.condition] ?? g.condition} calls, oldest first`} className="-ml-[3px] flex flex-wrap px-[3px]">
              {g.calls.map((c) => (
                <Square key={keyOf(c)} c={c} active={picked === keyOf(c)} onPick={() => setPicked(keyOf(c))} />
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p aria-live="polite" className={`${NOTE} mt-2 min-h-[1.5em] tabular-nums`}>
        {current ? detail(current) : 'Oldest first. Tap a square for its day.'}
      </p>
      <ul className={`${NOTE} mt-2 flex flex-wrap gap-x-4 gap-y-1`}>
        {ORDER.filter((o) => counts[o] > 0).map((o) => (
          <li key={o} className="inline-flex items-center gap-1.5 tabular-nums">
            <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-[2px] ${OUTCOME_SQUARE[o]}`} />
            {fmtCount(counts[o])} {OUTCOME_WORDS[o]}
          </li>
        ))}
      </ul>
    </div>
  );
}
